import { useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Loader,
  Alert,
  Box,
  Badge,
  Anchor,
  Tabs,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconDeviceTv,
  IconSettings,
  IconWorld,
} from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  hasAnyAppAccess,
  isAppPlatformAllowed,
} from '@lib2/model/customer/QuotaFeaturesAppUtil';
import { AppPlatformTypeId } from '@lib2/model/apps/AppPlatformType';
import { APP_STATE_TYPE_ID } from '@/lib/constants/app';
import { useApp } from '../useApp';
import { useUpdateApp } from '../useAppMutations';
import { AppUtils } from '../AppUtils';
import { appGeneralFormSchema } from '../schemas/AppGeneralForm.schema';
import { AppGeneralTab } from './AppGeneralTab';
import { AppWebTab } from './AppWebTab';
import { AppRokuTab } from './AppRokuTab';
import type { AppGeneralFormData } from '@/lib/types/App';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

export function AppDetailPage() {
  const { id: displayId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { selectedCustomer } = useAuth();

  // Parse base-converted ID
  let appCfgId = 0;
  let idError = false;
  try {
    appCfgId = AppUtils.getNumericId(displayId!);
    if (!appCfgId || appCfgId <= 0) idError = true;
  } catch {
    idError = true;
  }

  const { data: appFiles, isLoading, isError, error } = useApp(appCfgId);
  const updateMutation = useUpdateApp(appCfgId);

  // Quota checks
  const quotaApp = selectedCustomer?.cfg.quota.features.app;
  const hasAccess = !!quotaApp && hasAnyAppAccess(quotaApp);
  const webAllowed = !!quotaApp && isAppPlatformAllowed(quotaApp, AppPlatformTypeId.Web);
  const rokuAllowed = !!quotaApp && isAppPlatformAllowed(quotaApp, AppPlatformTypeId.Roku);

  const app = appFiles?.appCfgDto ?? null;
  const isActive = app
    ? app.appCfg.core.appStateTypeId === APP_STATE_TYPE_ID.Active
    : false;

  // Initialize form with app data
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<AppGeneralFormData>({
    resolver: zodResolver(appGeneralFormSchema),
    defaultValues: {
      name: '',
      desc: '',
      websiteDomainPath: '',
      rootLlId: 0,
      defaultPlayerId: 0,
      isChildTargeted: false,
      spotlightCatLLId: 'null',
      heroStyle: 'card',
      fbId: '',
      igId: '',
      xId: '',
      ytId: '',
      gaId: '',
    },
  });

  // Sync form when app data loads
  useEffect(() => {
    if (app) {
      reset(AppUtils.transformAppToFormData(app));
    }
  }, [app, reset]);

  const handleSave = handleSubmit((data: AppGeneralFormData) => {
    if (!app) return;
    const req = AppUtils.transformFormToUpdateReq(data, app);
    updateMutation.mutate(req, {
      onSuccess: () => {
        // Clean baseline immediately — the refetch-driven effect reset arrives
        // later; without this the save bar stays up for the whole round-trip.
        reset(data);
        notifications.show({
          title: 'Saved',
          message: 'App settings updated successfully.',
          color: 'green',
        });
      },
      onError: (err) => {
        const msg =
          err.message?.includes('409') || err.message?.includes('conflict')
            ? 'This app was modified in another session. Please refresh and try again.'
            : err.message;
        notifications.show({ title: 'Error', message: msg, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    if (app) {
      reset(AppUtils.transformAppToFormData(app));
    }
  }, [app, reset]);

  // ── Error states ──

  if (idError) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps' })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Apps
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Invalid app ID.
        </Alert>
      </Stack>
    );
  }

  if (!hasAccess) {
    return (
      <Stack>
        <Title order={2}>App</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          Your current plan does not include access to Apps.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading app...</Text>
      </Stack>
    );
  }

  if (isError || !app) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps' })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Apps
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message ?? 'Failed to load app'}
        </Alert>
      </Stack>
    );
  }

  const appDisplayId = AppUtils.getDisplayId(app.appCfg.core.appCfgId);

  return (
    <Stack gap="md">
      {/* Back link */}
      <Box>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps' })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Apps
        </Anchor>
      </Box>

      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{app.appCfg.orgInfo.name}</Title>
          <Group gap="xs" mt={4}>
            <Text size="sm" c="dimmed" ff="monospace">
              {appDisplayId}
            </Text>
            <Badge variant="light" color={isActive ? 'green' : 'gray'} size="sm">
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Box>
        <Group gap="xs">
          {app.web ? (
            <Badge
              variant="light"
              color={app.web.core.appStateTypeId === APP_STATE_TYPE_ID.Active ? 'blue' : 'orange'}
              size="sm"
              leftSection={<IconWorld size={12} />}
            >
              {app.web.core.appStateTypeId === APP_STATE_TYPE_ID.Active ? 'Web' : 'Web (inactive)'}
            </Badge>
          ) : webAllowed ? (
            <Badge
              variant="outline"
              color="gray"
              size="sm"
              leftSection={<IconWorld size={12} />}
            >
              Web (not enabled)
            </Badge>
          ) : null}
          {app.roku ? (
            <Badge
              variant="light"
              color={
                app.roku.core.appStateTypeId === APP_STATE_TYPE_ID.Active ? 'violet' : 'orange'
              }
              size="sm"
              leftSection={<IconDeviceTv size={12} />}
            >
              {app.roku.core.appStateTypeId === APP_STATE_TYPE_ID.Active
                ? 'Roku'
                : 'Roku (inactive)'}
            </Badge>
          ) : rokuAllowed ? (
            <Badge
              variant="outline"
              color="gray"
              size="sm"
              leftSection={<IconDeviceTv size={12} />}
            >
              Roku (not enabled)
            </Badge>
          ) : null}
        </Group>
      </Group>

      {/* Tabbed Content */}
      <Tabs defaultValue="general" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>
            General
          </Tabs.Tab>
          <Tabs.Tab value="web" leftSection={<IconWorld size={16} />}>
            Web
          </Tabs.Tab>
          <Tabs.Tab value="roku" leftSection={<IconDeviceTv size={16} />}>
            Roku
          </Tabs.Tab>
        </Tabs.List>

        {/* General Settings Tab */}
        <Tabs.Panel value="general" keepMounted>
          <form onSubmit={handleSave}>
            <FormSaveBar
              isDirty={isDirty}
              isPending={updateMutation.isPending}
              onDiscard={handleDiscard}
            />

            <Box mt="md">
              <AppGeneralTab control={control} watch={watch} />
            </Box>
          </form>
        </Tabs.Panel>

        {/* Web Platform Tab */}
        <Tabs.Panel value="web" keepMounted>
          <Box mt="md">
            <AppWebTab
              appCfgId={appCfgId}
              app={app}
              isAppActive={isActive}
              webAllowed={webAllowed}
              pfiles={appFiles!.pfiles}
              cId={selectedCustomer!.cfg.cId}
            />
          </Box>
        </Tabs.Panel>

        {/* Roku Platform Tab */}
        <Tabs.Panel value="roku" keepMounted>
          <Box mt="md">
            <AppRokuTab
              appCfgId={appCfgId}
              app={app}
              isAppActive={isActive}
              rokuAllowed={rokuAllowed}
            />
          </Box>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
